import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { screenSizes } from 'src/assets/styles/Literals';

const fadeUp = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const reveal = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const ServiceFormPageContainer = styled.div`
  --sf-accent: #2596c2;
  --sf-accent-soft: rgba(37, 150, 194, 0.08);
  --sf-text: #23323f;
  --sf-muted: #66737f;
  --sf-border: #e1e7ed;
  --sf-surface: #ffffff;
  --sf-danger: #d93a3a;

  width: 100%;
  min-height: 100vh;
  background: linear-gradient(180deg, #eef5f9 0%, #f5f5f5 320px);
  color: var(--sf-text);

  .service-form__inner {
    max-width: 1080px;
    margin: 0 auto;
    padding: 1.5rem 1.25rem 0;

    @media (max-width: ${screenSizes.tablet}) {
      padding: 1rem 0.75rem 0;
    }
  }
`;

export const PageHeader = styled.header`
  margin-bottom: 1.75rem;
  animation: ${fadeUp} 0.35s ease-out;

  .back-link {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0;
    border: 0;
    background: none;
    color: var(--sf-accent);
    font-weight: 600;
    cursor: pointer;
    margin-bottom: 0.75rem;
  }

  h1 {
    font-family: 'Raleway', sans-serif;
    font-size: 2rem;
    margin: 0 0 0.35rem;
    color: var(--sf-text);

    @media (max-width: ${screenSizes.tablet}) {
      font-size: 1.6rem;
    }
  }

  p {
    margin: 0;
    color: var(--sf-muted);
    font-size: 1.05rem;
  }
`;

export const FormLayout = styled.div`
  display: grid;
  grid-template-columns: 200px minmax(0, 1fr);
  gap: 2rem;
  align-items: start;

  @media (max-width: ${screenSizes.laptop}) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const SectionNav = styled.nav`
  position: sticky;
  top: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;

  @media (max-width: ${screenSizes.laptop}) {
    display: none;
  }

  button {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.55rem 0.75rem;
    border: 0;
    border-left: 2px solid var(--sf-border);
    background: none;
    color: var(--sf-muted);
    font-size: 0.95rem;
    text-align: left;
    cursor: pointer;
    transition: color 0.15s ease, border-color 0.15s ease, background 0.15s ease;

    &:hover {
      color: var(--sf-text);
      border-left-color: var(--sf-accent);
      background: var(--sf-accent-soft);
    }

    span {
      font-variant-numeric: tabular-nums;
      font-weight: 700;
      color: var(--sf-accent);
    }
  }
`;

export const SectionBlock = styled.section`
  position: relative;
  background: var(--sf-surface);
  border: 1px solid var(--sf-border);
  border-radius: 10px;
  padding: 1.5rem;
  margin-bottom: 1.25rem;
  scroll-margin-top: 1.5rem;
  overflow: visible;
  animation: ${fadeUp} 0.4s ease-out both;

  &:focus-within,
  &.is-raised {
    z-index: 3;
  }

  @media (max-width: ${screenSizes.tablet}) {
    padding: 1.1rem;
  }

  .section__header {
    display: flex;
    gap: 0.85rem;
    margin-bottom: 1.25rem;
  }

  .section__number {
    flex: none;
    width: 2rem;
    height: 2rem;
    border-radius: 6px;
    display: grid;
    place-items: center;
    background: var(--sf-accent-soft);
    color: var(--sf-accent);
    font-weight: 700;
  }

  h2 {
    font-family: 'Raleway', sans-serif;
    font-size: 1.25rem;
    margin: 0 0 0.2rem;
    color: var(--sf-text);
  }

  .section__description {
    margin: 0;
    color: var(--sf-muted);
  }
`;

export const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.1rem 1.25rem;
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;

  &.field--wide {
    grid-column: 1 / -1;
  }

  > label {
    font-weight: 600;
    color: var(--sf-text);
  }

  .field__hint {
    margin: 0;
    font-size: 0.875rem;
    color: var(--sf-muted);
  }

  .field__error {
    margin: 0;
    font-size: 0.875rem;
    color: var(--sf-danger);
  }

  .field__row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
  }

  .SingleDatePicker {
    position: relative;
    z-index: 2;
  }

  .SingleDatePicker_picker {
    z-index: 5;
  }

  .SingleDatePickerInput {
    border-radius: 4px;
  }

  &.field--error .SingleDatePickerInput {
    border-color: var(--sf-danger);
  }
`;

export const ToggleList = styled.div`
  display: flex;
  flex-direction: column;

  > * + * {
    border-top: 1px solid var(--sf-border);
  }
`;

export const ToggleRow = styled.div`
  padding: 0.9rem 0;

  &:first-of-type {
    padding-top: 0;
  }

  &:last-of-type {
    padding-bottom: 0;
  }

  .toggle__hint {
    margin: 0.5rem 0 0 3.6rem;
    color: var(--sf-muted);
    font-size: 0.9rem;
    animation: ${reveal} 0.2s ease-out;
  }

  .toggle__body {
    margin: 0.85rem 0 0 3.6rem;
    padding-left: 1rem;
    border-left: 2px solid var(--sf-accent);
    animation: ${reveal} 0.2s ease-out;

    @media (max-width: ${screenSizes.tablet}) {
      margin-left: 0.5rem;
    }
  }
`;

export const QuestionList = styled.ol`
  list-style: none;
  margin: 0 0 1rem;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export const QuestionRow = styled.li`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
  padding: 0.75rem;
  border: 1px solid var(--sf-border);
  border-radius: 8px;
  background: #fafcfd;
  animation: ${reveal} 0.2s ease-out;

  .question__index {
    flex: none;
    width: 1.5rem;
    font-weight: 700;
    color: var(--sf-accent);
    text-align: center;
  }

  .question__label {
    flex: 1 1 220px;
    min-width: 0;

    &.ui.input,
    &.ui.input > input {
      width: 100%;
    }
  }

  .question__type {
    flex: 0 1 180px;
    min-width: 140px;

    &.ui.dropdown {
      width: 100%;
    }
  }

  .question__required {
    flex: none;
  }

  .question__remove {
    flex: none;
    margin-left: auto;
  }

  .question__error {
    flex: 1 0 100%;
    margin: 0;
    font-size: 0.875rem;
    color: var(--sf-danger);
  }
`;

export const EmptyState = styled.p`
  margin: 0 0 1rem;
  padding: 1rem;
  border: 1px dashed var(--sf-border);
  border-radius: 8px;
  color: var(--sf-muted);
  text-align: center;
`;

export const ActionBar = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 5;
  margin-top: 1rem;
  background: rgba(255, 255, 255, 0.96);
  border-top: 1px solid var(--sf-border);
  backdrop-filter: blur(6px);

  .action-bar__inner {
    padding: 0.85rem 0;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.75rem;
    flex-wrap: wrap;
  }

  .action-bar__error {
    margin: 0 auto 0 0;
    color: var(--sf-danger);
    font-weight: 600;
  }
`;
